/**
 * LandingNav Component
 *
 * Sticky navigation with scroll-triggered transformation.
 * - At top: transparent background, full-width, dark logo
 * - Scrolling down: hides
 * - Scrolling up: shows as floating pill with dark background
 */
"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import AccountMenu from "./AccountMenu";
import { LearnNavLink } from "@/components/molecules/learn/LearnNavLink";
import {
  trackLandingNavClicked,
  trackLandingLoginClicked,
} from "@/lib/analytics/client";
import { LEARN_CTA_HREF, learnNavItems, type LearnNavSection } from "@/lib/learn/nav";
import { LEARN_EYEBROW, LEARN_TRY_LEV_CLASS } from "@/components/molecules/learn/learnStyles";
import { buildMidChatSignInHref } from "@/lib/onboarding/handoff";
import { useLandingChatPresenceStore } from "@/state/landingChatPresenceStore";

export interface LandingNavProps {
  /** Whether user is logged in */
  isLoggedIn: boolean;
  /** User initials for account menu */
  userInitials?: string;
  /** User avatar URL for account menu */
  userAvatarUrl?: string | null;
  /** Max width for desktop nav at top of page. */
  desktopMaxWidth?: number;
  /** Keep "How it Works" visible even for authenticated users. */
  showHowItWorksWhenLoggedIn?: boolean;
  /** Desktop horizontal padding for top (non-scrolled) nav layout. */
  desktopTopPaddingX?: number;
  /** Hide section links (How it Works, FAQs, Founder Apps, Learn). */
  hideNavLinks?: boolean;
  /**
   * Render the Lev Learn section nav (`LEARN_NAV_ITEMS`) instead of the marketing section
   * links, with this section marked active: the Lev Learn bar, rendered by every
   * Learn and Venture Graph page (`LearnHeader`, `VentureGraphHeader`, and the
   * `/learn` index through `LandingShell`). It shows Try Lev rather than Login,
   * so it needs no session. Takes effect only when `hideNavLinks` is also set,
   * since the two link sets occupy the same slot.
   */
  learnNavActive?: LearnNavSection;
  /**
   * Render the Lev Learn bar with no section marked active: for a page that
   * uses the Learn chrome but sits outside its sections (/mcp). Like
   * `learnNavActive`, it takes effect only with `hideNavLinks`.
   */
  learnNav?: boolean;
  /**
   * Include the Venture Graph link in that nav. A prop rather than a read of
   * `isVentureGraphPublic()` because this is a client component and the env is
   * server-only; the server page that renders it decides.
   */
  showVentureGraphNav?: boolean;
  /** Text of the link that scrolls to the How it Works section. */
  howItWorksLabel?: string;
}

/** The logo lockup. On every bar it links to the Lev home page. */
function Brand({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <Link href="/" aria-label="Lev home" className={className}>
      {children}
    </Link>
  );
}

export function LandingNav({
  isLoggedIn,
  userInitials = "",
  userAvatarUrl,
  desktopMaxWidth = 1240,
  showHowItWorksWhenLoggedIn = false,
  desktopTopPaddingX = 0,
  hideNavLinks = false,
  learnNavActive,
  learnNav = false,
  showVentureGraphNav = false,
  howItWorksLabel = "How it Works",
}: LandingNavProps) {
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  // null = no landing chat has a user message on screen; a bare /sign-in is
  // fine then. Any other value (including "" for the home landing) means a
  // sign-in here must resume that chat via /onboarding/complete (LEV-1925).
  const midChatSlug = useLandingChatPresenceStore((s) => s.midChatSlug);
  const loginHref = midChatSlug !== null ? buildMidChatSignInHref(midChatSlug) : "/sign-in";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 100;

      if (currentScrollY < scrollThreshold) {
        // At top of page - always show transparent nav
        setShowNav(true);
        setIsScrolled(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show pill nav
        setShowNav(true);
        setIsScrolled(true);
      } else {
        // Scrolling down - hide nav
        setShowNav(false);
        setIsScrolled(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (sectionId: string) => {
    trackLandingNavClicked({ section: sectionId });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // The Lev Learn bar: the same floating nav on every Learn and Venture Graph
  // page (Concepts, Guides, concept pages, Venture Graph), so the family looks
  // and scrolls as one. It shows Try Lev rather than Login, so it never needs
  // to know who is signed in, which is what lets the static Learn pages render
  // it and stay prerendered.
  // `learnMode` decides whether the Learn bar renders; `learnActive` is the
  // section to mark current, absent on a Learn-chrome page outside the
  // sections, which still gets the full bar.
  const learnMode = hideNavLinks && (learnNav || learnNavActive !== undefined);
  const learnActive = learnMode ? learnNavActive : undefined;

  const navLinkClasses = `text-sm whitespace-nowrap transition-colors duration-300 hover:text-lev-orange ${
    isScrolled ? "text-white" : "text-landing-dark"
  }`;

  return (
    <>
      {/* Desktop Nav */}
      <div
        className={`hidden md:flex fixed left-0 right-0 z-50 justify-center transition-all duration-500 ease-out ${
          learnMode ? "px-0" : "px-4 md:px-6"
        } ${
          showNav ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
        style={{ top: learnMode ? "0px" : "16px" }}
      >
        <nav
          aria-label={learnMode ? "Lev Learn" : undefined}
          className={`flex w-full transition-all duration-700 ease-out ${
            // Baseline-aligned only on /learn: the logo, "Learn" wordmark, and
            // nav links all need to sit on one shared bottom edge (operator
            // feedback — the default vertical centering misaligned the text
            // against the logo). Every other lander keeps the plain
            // vertical-center row, unaffected.
            learnMode ? "items-baseline" : "items-center"
          }`}
          style={{
            backgroundColor: isScrolled ? "#2D3139" : "transparent",
            padding: isScrolled
              ? "12px 28px"
              : learnMode
                ? "20px 20px"
                : `16px ${desktopTopPaddingX}px`,
            borderRadius: isScrolled ? "9999px" : "0px",
            width: "100%",
            maxWidth: isScrolled ? "720px" : learnMode ? "1032px" : `${desktopMaxWidth}px`,
            boxShadow: isScrolled
              ? "0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)"
              : "none",
          }}
        >
          {/* Logo and, on /learn, the "Learn" wordmark share one baseline —
              items-baseline rather than the row's default items-center, so a
              replaced element (the logo) and text line up on their shared
              bottom edge instead of their vertical centers. Scoped to this
              inner group so the button on the right still centers normally. */}
          <Brand className="flex flex-shrink-0 items-baseline gap-2.5">
            <Image
              src={isScrolled ? "/landing/lev-logo-white.svg" : "/landing/lev-logo-dark.svg"}
              alt="Lev"
              width={isScrolled ? 60 : learnMode ? 62 : 70}
              height={isScrolled ? 24 : learnMode ? 26 : 28}
              className={`transition-all duration-700 ${!isScrolled && learnMode ? "w-[54px]" : ""}`}
            />
            {learnMode && (
              <span className={LEARN_EYEBROW}>Learn</span>
            )}
          </Brand>
          {/* Left-anchored, right after the logo/wordmark — reads as one
              group ("Lev Learn Concepts Guides Venture Graph"). The marketing
              section links below stay
              right-anchored via ml-auto; this and that are mutually
              exclusive (hideNavLinks gates both). */}
          {learnMode && (
            <div className="ml-6 flex items-baseline gap-6 transition-all duration-700">
              {learnNavItems(showVentureGraphNav).map((item) => (
                <LearnNavLink
                  key={item.section}
                  section={item.section}
                  href={item.href}
                  label={item.label}
                  active={learnActive}
                  className={`whitespace-nowrap border-b-2 pb-0.5 text-[14px] font-semibold transition-colors duration-300 ${
                    item.section === learnActive
                      ? `border-lev-orange ${isScrolled ? "text-white" : "text-landing-dark"}`
                      : isScrolled
                        ? "border-transparent text-white/70 hover:text-white"
                        : "border-transparent text-landing-secondary hover:text-landing-dark"
                  }`}
                />
              ))}
            </div>
          )}
          <div
            className="ml-auto flex items-center transition-all duration-700"
            style={{ gap: isScrolled ? "20px" : "32px" }}
          >
            {!hideNavLinks && (
              <>
                {(!isLoggedIn || showHowItWorksWhenLoggedIn) && (
                  <button type="button" onClick={() => scrollToSection("how-it-works")} className={navLinkClasses}>
                    {howItWorksLabel}
                  </button>
                )}
                <button type="button" onClick={() => scrollToSection("faq")} className={navLinkClasses}>
                  FAQs
                </button>
                <button type="button" onClick={() => scrollToSection("founder-apps")} className={navLinkClasses}>
                  Founder Apps
                </button>
                <Link
                  href="/learn"
                  onClick={() => trackLandingNavClicked({ section: "learn" })}
                  className={navLinkClasses}
                >
                  Learn
                </Link>
              </>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {learnMode ? (
                <Link href={LEARN_CTA_HREF} className={LEARN_TRY_LEV_CLASS}>
                  Try Lev
                </Link>
              ) : isLoggedIn ? (
                <AccountMenu initials={userInitials} avatarUrl={userAvatarUrl} />
              ) : (
                <Link
                  href={loginHref}
                  onClick={() => trackLandingLoginClicked()}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    isScrolled
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-[#E3E5E8] text-landing-dark hover:bg-[#D5D7DB]"
                  }`}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Nav */}
      <div
        className={`md:hidden fixed left-0 right-0 z-50 flex justify-center px-4 transition-all duration-500 ease-out ${
          showNav ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
        style={{ top: "12px" }}
      >
        <nav
          aria-label={learnMode ? "Lev Learn" : undefined}
          className={`flex w-full transition-all duration-700 ease-out ${
            learnMode ? "items-baseline" : "items-center"
          }`}
          style={{
            backgroundColor: isScrolled ? "#2D3139" : "transparent",
            padding: isScrolled ? "10px 16px" : "16px 0px",
            borderRadius: isScrolled ? "9999px" : "0px",
            width: "100%",
            // Widened when the Learn nav is present — 280px fit logo + Login
            // alone, not two more nav links, and this pill has no wrap
            // fallback. Still just an upper bound: the real width is capped
            // by the viewport (px-4 on the wrapping div below), which is why
            // the wordmark above also drops out once scrolled.
            maxWidth: isScrolled ? (learnMode ? "360px" : "280px") : "100%",
            boxShadow: isScrolled
              ? "0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)"
              : "none",
          }}
        >
          <Brand className="flex items-baseline gap-1.5">
            <Image
              src={isScrolled ? "/landing/lev-logo-white.svg" : "/landing/lev-logo-dark.svg"}
              alt="Lev"
              width={isScrolled ? 50 : 60}
              height={isScrolled ? 20 : 24}
              className={`transition-all duration-700 ${
                learnMode ? (isScrolled ? "max-[359px]:hidden" : "max-[359px]:w-11") : ""
              }`}
            />
            {/* Dropped once scrolled: the pill's hard maxWidth (below) leaves
                no room for the wordmark plus two nav links plus Login at a
                320px viewport (Copilot review, PR #860 — confirmed by pixel
                budget, not just eyeballed). The unscrolled top state has no
                such cap, so it keeps the wordmark. */}
            {learnMode && !isScrolled && (
              <span className="hidden text-[11px] font-bold uppercase tracking-[0.1em] text-lev-orange min-[400px]:inline">
                Learn
              </span>
            )}
          </Brand>
          {learnMode && (
            // Four links plus logo and Try Lev overflow a 375px pill, which
            // has no wrap fallback: the links scroll sideways inside their
            // own row instead of pushing Try Lev off screen.
            <div className="ml-2 flex min-w-0 items-baseline gap-1.5 overflow-x-auto [scrollbar-width:none] max-[359px]:ml-1.5 max-[359px]:gap-1">
              {learnNavItems(showVentureGraphNav).map((item) => (
                <LearnNavLink
                  key={item.section}
                  section={item.section}
                  href={item.href}
                  label={item.label}
                  active={learnActive}
                  className={`whitespace-nowrap border-b-2 pb-0.5 text-[12px] font-semibold transition-colors duration-300 max-[359px]:text-[11px] ${
                    item.section === learnActive
                      ? `border-lev-orange ${isScrolled ? "text-white" : "text-landing-dark"}`
                      : isScrolled
                        ? "border-transparent text-white/70"
                        : "border-transparent text-landing-secondary"
                  }`}
                />
              ))}
            </div>
          )}
          <div className="ml-auto flex-shrink-0">
            {learnMode ? (
              // Tighter below 360px: logo, three nav links and this button
              // otherwise touch at 320px (no wrap fallback in the pill).
              <Link href={LEARN_CTA_HREF} className={`${LEARN_TRY_LEV_CLASS} max-[359px]:px-2`}>
                Try Lev
              </Link>
            ) : isLoggedIn ? (
              <AccountMenu initials={userInitials} avatarUrl={userAvatarUrl} />
            ) : (
              <Link
                href={loginHref}
                onClick={() => trackLandingLoginClicked()}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  isScrolled
                    ? "bg-white text-landing-dark"
                    : "bg-landing-dark text-white"
                }`}
              >
                Login
              </Link>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}
